import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    GoogleMap,
    InfoWindow,
    Marker,
    StandaloneSearchBox,
    useLoadScript,
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { getPropertyImageUrl } from "../../../../utils/property-image";
import "./PropertyMapView.css";

const GOOGLE_MAPS_LIBRARIES = ["places"];

// Default centre: Kathmandu, Nepal
const NEPAL_CENTER = { lat: 27.7172, lng: 85.324 };

/**
 * PropertyMapView
 *
 * Props:
 *   properties   {Array}     – full list of property objects (may include lat/lng)
 *   onBoundsChanged {function} – optional, called with (bounds) when map viewport changes
 */
function PropertyMapView({ properties = [], onBoundsChanged }) {
    const navigate = useNavigate();
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: apiKey || "",
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const mapRef = useRef(null);
    const searchBoxRef = useRef(null);
    const hoverCloseTimerRef = useRef(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [geocodedCoords, setGeocodedCoords] = useState({});
    const [isLegendMinimized, setIsLegendMinimized] = useState(false);

    const BRAND_ORANGE = "#f06c24";
    const BRAND_NAVY = "#0f2747";

    const getPropertyId = useCallback((property) => {
        return String(property?.id ?? property?._id ?? "");
    }, []);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    const getCoordinates = useCallback((property) => {
        const latCandidate = property?.lat ?? property?.latitude ?? property?.propertyLat;
        const lngCandidate = property?.lng ?? property?.longitude ?? property?.propertyLng;
        const lat = Number(latCandidate);
        const lng = Number(lngCandidate);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
        return { lat, lng };
    }, []);

    const buildAddressQuery = useCallback((property) => {
        return [
            property?.address,
            property?.municipality,
            property?.city,
            property?.district,
            property?.state,
            "Nepal",
        ]
            .filter(Boolean)
            .join(", ");
    }, []);

    const getTypeMarkerMeta = useCallback((type) => {
        const normalized = String(type || "").toLowerCase().trim();
        switch (normalized) {
            case "house":
                return { iconKey: "house" };
            case "apartment":
                return { iconKey: "apartment" };
            case "villa":
                return { iconKey: "villa" };
            case "studio":
                return { iconKey: "studio" };
            case "cottage":
                return { iconKey: "cottage" };
            case "land":
                return { iconKey: "land" };
            case "commercial":
                return { iconKey: "commercial" };
            case "condo":
                return { iconKey: "condo" };
            case "cabin":
                return { iconKey: "cabin" };
            case "room":
                return { iconKey: "room" };
            default:
                return { iconKey: "default" };
        }
    }, []);

    const getTypeLabel = useCallback((type) => {
        const normalized = String(type || "").toLowerCase().trim();
        if (!normalized) return "Property";
        return normalized
            .split(" ")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }, []);

    const getIconMarkup = useCallback((iconKey) => {
        switch (iconKey) {
            case "house":
                return '<path d="M12 23v-5.8L20 11l8 6.2V23h-5.5v-4.2h-5V23z" fill="#0f2747"/>';
            case "apartment":
                return '<rect x="13" y="12" width="14" height="12" rx="1.2" fill="#0f2747"/><rect x="16" y="15" width="2" height="2" fill="#fff"/><rect x="20" y="15" width="2" height="2" fill="#fff"/><rect x="24" y="15" width="2" height="2" fill="#fff"/><rect x="16" y="19" width="2" height="2" fill="#fff"/><rect x="20" y="19" width="2" height="2" fill="#fff"/>';
            case "villa":
                return '<path d="M11 22v-4.8L20 10l9 7.2V22h-4.8v-3.8h-8.4V22z" fill="#0f2747"/><circle cx="10.5" cy="20.5" r="2.5" fill="#0f2747"/>';
            case "studio":
                return '<rect x="12" y="14" width="16" height="10" rx="2" fill="#0f2747"/><circle cx="20" cy="19" r="3" fill="#fff"/><circle cx="20" cy="19" r="1.6" fill="#0f2747"/><rect x="14" y="12" width="4" height="2" fill="#0f2747"/>';
            case "cottage":
                return '<path d="M12.5 22v-4.5L20 12l7.5 5.5V22h-3.8v-3h-7.4v3z" fill="#0f2747"/><path d="M14.5 14.2h11" stroke="#0f2747" stroke-width="1.4"/>';
            case "land":
                return '<path d="M20 24c-4.4-2-5.8-5.2-4.8-9.2 4.1.5 6.6 2.9 7 6.8" fill="none" stroke="#0f2747" stroke-width="1.7" stroke-linecap="round"/><path d="M20 24c4.4-2 5.8-5.2 4.8-9.2-4.1.5-6.6 2.9-7 6.8" fill="none" stroke="#0f2747" stroke-width="1.7" stroke-linecap="round"/>';
            case "commercial":
                return '<rect x="13" y="15" width="14" height="9" rx="1.8" fill="#0f2747"/><rect x="17" y="13" width="6" height="2" rx="1" fill="#0f2747"/><rect x="19" y="18" width="2" height="2" fill="#fff"/>';
            case "condo":
                return '<rect x="14" y="11" width="12" height="13" rx="1.2" fill="#0f2747"/><rect x="16.5" y="14" width="2" height="2" fill="#fff"/><rect x="21.5" y="14" width="2" height="2" fill="#fff"/><rect x="16.5" y="18" width="2" height="2" fill="#fff"/><rect x="21.5" y="18" width="2" height="2" fill="#fff"/>';
            case "cabin":
                return '<path d="M13 22v-4.8L20 12l7 5.2V22h-3.5v-3.2h-7V22z" fill="#0f2747"/><path d="M12.8 17.7h14.4M12.8 19.6h14.4" stroke="#fff" stroke-width="1.2"/>';
            case "room":
                return '<rect x="12.5" y="18" width="15" height="4" rx="1.2" fill="#0f2747"/><rect x="13.5" y="16" width="5.2" height="2" rx="0.7" fill="#0f2747"/><rect x="12.5" y="22" width="1.6" height="2" fill="#0f2747"/><rect x="25.9" y="22" width="1.6" height="2" fill="#0f2747"/>';
            default:
                return '<circle cx="20" cy="19" r="3.2" fill="#0f2747"/>';
        }
    }, []);

    const makeMarkerIcon = useCallback((type, selected = false) => {
        const { iconKey } = getTypeMarkerMeta(type);
        const scale = selected ? 44 : 38;
        const fillColor = selected ? BRAND_NAVY : BRAND_ORANGE;
        const ring = selected ? BRAND_ORANGE : BRAND_NAVY;
        const iconColor = selected ? BRAND_ORANGE : BRAND_NAVY;
        const iconMarkup = getIconMarkup(iconKey).replace(/#0f2747/g, iconColor);
        const svg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="${scale}" height="${scale}" viewBox="0 0 40 46">
            <path d="M20 2C11.2 2 4 9.2 4 18c0 11.3 14.3 23.7 15 24.2a1.6 1.6 0 0 0 2 0C21.7 41.7 36 29.3 36 18 36 9.2 28.8 2 20 2z" fill="${fillColor}" stroke="${ring}" stroke-width="2"/>
            <circle cx="20" cy="18" r="9.2" fill="#ffffff"/>
            ${iconMarkup}
          </svg>
        `;
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new window.google.maps.Size(scale, scale),
            anchor: new window.google.maps.Point(scale / 2, scale - 2),
        };
    }, [BRAND_NAVY, BRAND_ORANGE, getIconMarkup, getTypeMarkerMeta]);

    const cancelHoverClose = useCallback(() => {
        if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
        }
    }, []);

    const openPreview = useCallback((property) => {
        cancelHoverClose();
        setSelectedProperty(property);
    }, [cancelHoverClose]);

    const schedulePreviewClose = useCallback(() => {
        cancelHoverClose();
        hoverCloseTimerRef.current = setTimeout(() => {
            setSelectedProperty(null);
        }, 180);
    }, [cancelHoverClose]);

    useEffect(() => {
        return () => cancelHoverClose();
    }, [cancelHoverClose]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        setIsLegendMinimized(window.innerWidth <= 768);
    }, []);

    useEffect(() => {
        if (!isLoaded || !window.google || properties.length === 0) return;

        let cancelled = false;
        const geocoder = new window.google.maps.Geocoder();
        const missing = properties
            .filter((property) => !getCoordinates(property))
            .filter((property) => {
                const id = getPropertyId(property);
                if (!id) return false;
                return !geocodedCoords[id];
            })
            .slice(0, 30);

        const geocodeOne = (address) => {
            return new Promise((resolve) => {
                geocoder.geocode({ address }, (results, status) => {
                    if (status === "OK" && results?.[0]?.geometry?.location) {
                        const location = results[0].geometry.location;
                        resolve({ lat: location.lat(), lng: location.lng() });
                        return;
                    }
                    resolve(null);
                });
            });
        };

        (async () => {
            const updates = {};
            for (const property of missing) {
                const id = getPropertyId(property);
                const address = buildAddressQuery(property);
                if (!id || !address) continue;
                const coords = await geocodeOne(address);
                if (cancelled) return;
                if (coords) updates[id] = coords;
            }

            if (!cancelled && Object.keys(updates).length > 0) {
                setGeocodedCoords((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [buildAddressQuery, geocodedCoords, getCoordinates, getPropertyId, isLoaded, properties]);

    // Filter to only properties that have valid saved pin coordinates.
    const mappable = properties
        .map((property) => {
            const explicitCoords = getCoordinates(property);
            const fallbackCoords = geocodedCoords[getPropertyId(property)];
            const coords = explicitCoords || fallbackCoords || null;
            return coords ? { ...property, lat: coords.lat, lng: coords.lng } : null;
        })
        .filter(Boolean);

    const legendItems = [];
    const seenLegendTypes = new Set();
    mappable.forEach((property) => {
        const normalizedType = String(property?.type || "").toLowerCase().trim() || "default";
        if (seenLegendTypes.has(normalizedType)) return;
        seenLegendTypes.add(normalizedType);
        legendItems.push({
            key: normalizedType,
            label: getTypeLabel(property?.type),
            iconUrl: makeMarkerIcon(property?.type)?.url,
        });
    });

    const handlePlacesChanged = useCallback(() => {
        if (!searchBoxRef.current) return;
        const places = searchBoxRef.current.getPlaces();
        if (!places || places.length === 0) return;
        const place = places[0];
        if (!place.geometry?.location) return;

        const newLat = place.geometry.location.lat();
        const newLng = place.geometry.location.lng();

        if (mapRef.current) {
            mapRef.current.panTo({ lat: newLat, lng: newLng });
            mapRef.current.setZoom(14);
        }
    }, []);

    const handleBoundsChanged = useCallback(() => {
        if (!mapRef.current || !onBoundsChanged) return;
        const b = mapRef.current.getBounds();
        if (b) onBoundsChanged(b);
    }, [onBoundsChanged]);

    const handleViewDetails = (property) => {
        const id = property.id || property._id;
        sessionStorage.setItem(`property_${id}`, JSON.stringify(property));
        navigate(`/property/${id}`);
    };

    if (!apiKey) {
        return (
            <div className="map-view-api-missing">
                <span>&#9888;</span>
                <p>
                    Google Maps API key not configured.<br />
                    Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to your <code>.env</code> file.
                    <br />
                    <a
                        href="https://developers.google.com/maps/documentation/javascript/get-api-key"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Get an API key →
                    </a>
                </p>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="map-view-api-missing">
                <span>&#9888;</span>
                <p>Failed to load Google Maps. Check your API key and network.</p>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="map-view-loading">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                <span>Loading map…</span>
            </div>
        );
    }

    return (
        <div className="map-view-wrapper">
            {/* Floating search */}
            <div className="map-view-search-bar">
                <StandaloneSearchBox
                    onLoad={(ref) => { searchBoxRef.current = ref; }}
                    onPlacesChanged={handlePlacesChanged}
                >
                    <input
                        type="text"
                        placeholder="Search area (e.g. Pokhara, Lalitpur…)"
                        aria-label="Search map area"
                    />
                </StandaloneSearchBox>
            </div>

            <GoogleMap
                mapContainerClassName="map-view-canvas"
                center={
                    mappable.length > 0
                        ? { lat: mappable[0].lat, lng: mappable[0].lng }
                        : NEPAL_CENTER
                }
                zoom={mappable.length > 0 ? 13 : 12}
                onLoad={onMapLoad}
                onBoundsChanged={handleBoundsChanged}
                options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                }}
            >
                {mappable.map((property) => (
                    <Marker
                        key={property.id || property._id}
                        position={{ lat: property.lat, lng: property.lng }}
                        title={property.name}
                        onClick={() => openPreview(property)}
                        onMouseOver={() => openPreview(property)}
                        onMouseOut={schedulePreviewClose}
                        icon={makeMarkerIcon(property.type, String(selectedProperty?.id || selectedProperty?._id) === String(property.id || property._id))}
                    />
                ))}

                {selectedProperty && selectedProperty.lat && selectedProperty.lng && (
                    <InfoWindow
                        position={{ lat: selectedProperty.lat, lng: selectedProperty.lng }}
                        onCloseClick={() => setSelectedProperty(null)}
                    >
                        <div
                            className="map-info-card"
                            onMouseEnter={cancelHoverClose}
                            onMouseLeave={schedulePreviewClose}
                        >
                            {(selectedProperty.thumbnail || selectedProperty.photos?.[0]) && (
                                <img
                                    src={getPropertyImageUrl(
                                        selectedProperty.thumbnail || selectedProperty.photos[0],
                                        { width: 320 }
                                    )}
                                    alt={selectedProperty.name}
                                />
                            )}
                            <div className="info-name">{selectedProperty.name}</div>
                            <div className="info-location">
                                {selectedProperty.city}, {selectedProperty.state}
                            </div>
                            <div className="info-price">
                                NPR {selectedProperty.price?.toLocaleString()} / mo
                            </div>
                            <div className="info-meta">
                                {selectedProperty.bedrooms} bed &bull; {selectedProperty.bathrooms} bath
                                {selectedProperty.sqft ? ` \u2022 ${selectedProperty.sqft} sqft` : ""}
                            </div>
                            <button
                                className="info-btn"
                                onClick={() => handleViewDetails(selectedProperty)}
                            >
                                View Details
                            </button>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>

            <div className="map-view-count">
                {mappable.length} of {properties.length} properties shown on map
            </div>

            {legendItems.length > 0 && (
                <div className="map-view-legend-shell">
                    <button
                        type="button"
                        className="map-view-legend-toggle"
                        onClick={() => setIsLegendMinimized((prev) => !prev)}
                        aria-expanded={!isLegendMinimized}
                        aria-controls="map-view-legend-panel"
                    >
                        {isLegendMinimized ? "Show Legend" : "Hide Legend"}
                    </button>

                    {!isLegendMinimized && (
                        <div
                            id="map-view-legend-panel"
                            className="map-view-legend"
                            aria-label="Map marker legend"
                        >
                            <div className="map-view-legend-title">Map Legend</div>
                            <ul className="map-view-legend-list">
                                {legendItems.map((item) => (
                                    <li key={item.key} className="map-view-legend-item">
                                        <img src={item.iconUrl} alt="" aria-hidden="true" />
                                        <span>{item.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PropertyMapView;
